import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { DrilldownTicket, DrilldownResponse } from '../../services/sapInsightsService';

export interface DrilldownContext {
  fetchPage: (page: number) => Promise<DrilldownResponse>;
  title: string;
  showAutomationCols?: boolean;
}

interface Props {
  context: DrilldownContext | null;
  onClose: () => void;
}

function downloadCsv(tickets: DrilldownTicket[], title: string, showAutomationCols: boolean) {
  const headers = [
    'Ticket Number', 'Type', 'SNOW Link', 'Assignment Group',
    'State', 'Assigned To', 'Config Item', 'Short Description',
    'MTTR (h)', 'Reopen Count', 'CNE', 'Created Date', 'Resolved Date', 'Source',
    ...(showAutomationCols ? ['Outcome', 'Automation Name'] : []),
  ];
  const esc = (v: unknown) => {
    const s = v === null || v === undefined ? '' : String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = tickets.map((t) => [
    t.ticket_number, t.ticket_type, t.snow_url, t.assignment_group,
    t.state, t.assigned_to, t.cmdb_ci, t.short_description,
    t.mttr_hours != null ? t.mttr_hours.toFixed(1) : '',
    t.reopen_count, t.cne_raised ? 'Yes' : 'No',
    t.created_date ?? '', t.resolved_date ?? '', t.source,
    ...(showAutomationCols ? [t.outcome ?? '', t.automation_name ?? ''] : []),
  ].map(esc).join(','));
  const csv = [headers.join(','), ...rows].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${title.replace(/[^a-z0-9]+/gi, '_')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function TicketDrilldownModal({ context, onClose }: Props) {
  const [tickets, setTickets] = useState<DrilldownTicket[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!context) {
      setTickets([]);
      setTotal(0);
      setPage(1);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    setTickets([]);
    setTotal(0);
    setPage(1);
    context.fetchPage(1)
      .then((res) => { setTickets(res.tickets); setTotal(res.total); })
      .catch((err) => setError(err?.message || 'Failed to load tickets'))
      .finally(() => setLoading(false));
  }, [context]);

  const handleLoadMore = () => {
    if (!context || loadingMore) return;
    const next = page + 1;
    setLoadingMore(true);
    context.fetchPage(next)
      .then((res) => { setTickets((prev) => [...prev, ...res.tickets]); setPage(next); })
      .catch((err) => setError(err?.message || 'Failed to load more'))
      .finally(() => setLoadingMore(false));
  };

  const showAuto = !!context?.showAutomationCols;
  const hasMore = tickets.length < total;

  return (
    <Dialog
      open={!!context}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { height: '85vh', display: 'flex', flexDirection: 'column' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>{context?.title ?? ''}</Typography>
          {total > 0 && !loading && (
            <Typography variant="caption" color="text.secondary">{tickets.length} of {total} tickets</Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Export loaded rows as CSV">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                disabled={tickets.length === 0}
                onClick={() => downloadCsv(tickets, context?.title ?? 'tickets', showAuto)}
              >
                Export CSV
              </Button>
            </span>
          </Tooltip>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {error ? (
          <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
        ) : loading ? (
          <Stack alignItems="center" justifyContent="center" flex={1}>
            <CircularProgress size={40} />
          </Stack>
        ) : tickets.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ m: 3 }}>
            No tickets found for this selection.
          </Typography>
        ) : (
          <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, minWidth: 130 }}>Ticket #</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>Assignment Group</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>State</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 140 }}>Assigned To</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 140 }}>Config Item</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 220 }}>Short Description</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">MTTR (h)</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Reopens</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">CNE</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 100 }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 100 }}>Resolved</TableCell>
                  {showAuto && <TableCell sx={{ fontWeight: 700 }}>Outcome</TableCell>}
                  {showAuto && <TableCell sx={{ fontWeight: 700, minWidth: 160 }}>Automation Scope</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.map((t, i) => (
                  <TableRow key={`${t.ticket_number}-${i}`} hover>
                    <TableCell>
                      <Link href={t.snow_url} target="_blank" rel="noopener noreferrer" underline="hover">
                        {t.ticket_number}
                      </Link>
                    </TableCell>
                    <TableCell>{t.ticket_type}</TableCell>
                    <TableCell>{t.assignment_group}</TableCell>
                    <TableCell>{t.state}</TableCell>
                    <TableCell>{t.assigned_to}</TableCell>
                    <TableCell>{t.cmdb_ci}</TableCell>
                    <TableCell>
                      <Tooltip title={t.short_description || ''} arrow placement="top">
                        <Typography
                          variant="body2"
                          sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'default' }}
                        >
                          {t.short_description || '—'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">{t.mttr_hours != null ? t.mttr_hours.toFixed(1) : 'N/A'}</TableCell>
                    <TableCell align="center">{t.reopen_count}</TableCell>
                    <TableCell align="center">{t.cne_raised ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{t.created_date ?? '—'}</TableCell>
                    <TableCell>{t.resolved_date ?? '—'}</TableCell>
                    {showAuto && <TableCell>{t.outcome ?? '—'}</TableCell>}
                    {showAuto && <TableCell>{t.automation_name ?? '—'}</TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {hasMore && !loading && (
          <Stack alignItems="center" p={1.5} borderTop="1px solid" borderColor="divider">
            <Button variant="outlined" size="small" onClick={handleLoadMore} disabled={loadingMore}>
              {loadingMore ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
              Load more ({total - tickets.length} remaining)
            </Button>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
